import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface StatItem {
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

export default function StatsSection() {
  const { t } = useTranslation('home');
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats: StatItem[] = [
    { value: 500, suffix: '万+', label: t('stats_users'), icon: 'ri-user-star-line' },
    { value: 100, suffix: '万+', label: t('stats_articles'), icon: 'ri-article-line' },
    { value: 30, suffix: '+', label: t('stats_matches'), icon: 'ri-trophy-line' },
    { value: 150, suffix: '+', label: t('stats_countries'), icon: 'ri-global-line' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const CountUp = ({ target }: { target: number }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!visible) return;
      const duration = 2000;
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }, [target, visible]);

    return <span>{count.toLocaleString()}</span>;
  };

  return (
    <section ref={sectionRef} className="w-full py-14 md:py-20 bg-primary-500 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-background-50"></div>
        <div className="absolute bottom-10 right-10 w-60 h-60 rounded-full bg-background-50"></div>
        <div className="absolute top-1/2 left-1/3 w-20 h-20 rounded-full bg-background-50"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
            {t('stats_title')}
          </h2>
          <p className="text-background-50/70 text-sm md:text-base">{t('stats_subtitle')}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-50/15">
                <i className={`${stat.icon} text-2xl text-background-50`}></i>
              </div>
              <div className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-1">
                {visible ? <CountUp target={stat.value} /> : '0'}{stat.suffix}
              </div>
              <div className="text-background-50/70 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}